import { useCallback, useEffect, useId } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';
import { useAlert } from '../components/common/alert/AlertProvider';
import { useLocalizedStrings } from '../localization/LocalizedStringsProvider';
import { useUnsavedChanges } from './unsavedChangesContext';

// Prompts "you have unsaved changes" before the user leaves a dirty form.
//
// Covers two distinct kinds of leave:
//   1. In-app route changes (left menu, Cancel, browser back/forward, any <Link>) via react-router's
//      useBlocker — which is why App.tsx uses a data router (createBrowserRouter).
//   2. Real page unloads (tab close, refresh, typing a new URL) via the native beforeunload event; useBlocker
//      does not see those. The browser shows its own generic prompt there — the message text can't be set.
//   3. Switching the org/brand/location scope, which is NOT a route change and so is invisible to useBlocker.
//      This hook publishes the dirty state to UnsavedChangesProvider and the scope selector asks before
//      switching. (This comment used to claim the blocker covered the scope selector. It never did.)
//
// Pass formState.isDirty from the page's react-hook-form. Reset the form (or navigate) after a successful
// save so isDirty clears and this stops prompting.
// `onDiscard` says how this screen leaves itself when the user answers Leave somewhere the router cannot see
// — today, the scope selector. Without it the prompt is answered and the screen stays put, still dirty and
// still showing the scope the user just navigated away from (TTD-149). A screen with nothing to do omits it.
//
// Wrap it in useCallback: an unstable identity re-registers on every render, which is harmless but pointless.
export function useUnsavedChangesPrompt(isDirty: boolean, onDiscard?: () => void): void {
    const alert = useAlert();
    const strings = useLocalizedStrings();
    const { setDirty, registerDiscard, isDiscarding } = useUnsavedChanges();
    // Stable per mounted screen, so two guarded screens register separately and unmounting always clears
    // this one's claim rather than the other's.
    const key = useId();

    useEffect(() => {
        setDirty(key, isDirty);
        return () => setDirty(key, false);
    }, [key, isDirty, setDirty]);

    useEffect(() => {
        registerDiscard(key, onDiscard);
        return () => registerDiscard(key, undefined);
    }, [key, onDiscard, registerDiscard]);

    // Only block when actually changing route — not for a search/hash change on the same page — and never
    // during a discard, where the user has already said Leave and is being taken off this screen because of it.
    const blocker = useBlocker(
        useCallback<BlockerFunction>(
            ({ currentLocation, nextLocation }) =>
                !isDiscarding() && isDirty && currentLocation.pathname !== nextLocation.pathname,
            [isDirty, isDiscarding]
        )
    );

    useEffect(() => {
        if (blocker.state !== 'blocked') return;

        // Guards against acting on a stale prompt if the block resolves while the dialog is open.
        let active = true;

        void (async () => {
            const leave = await alert({
                title: strings.unsavedChangesTitle,
                message: strings.unsavedChangesMessage,
                agreeButtonTitle: strings.unsavedChangesLeave,
                closeButtonTitle: strings.unsavedChangesStay,
                destructive: true,
            });

            if (!active) return;
            if (leave) blocker.proceed?.();
            else blocker.reset?.();
        })();

        return () => {
            active = false;
        };
        // Fire once per transition into 'blocked'. alert/strings are read from the current closure on purpose
        // (adding them as deps risks re-opening the dialog if their identity changes mid-block).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocker.state]);

    // Native guard for full page unloads, which useBlocker can't intercept.
    useEffect(() => {
        if (!isDirty) return;

        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);
}
